import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ListItem, SvgIcon, Modal, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useDirection } from '../../../hooks/useDirection'

import Avatar from '../avatar/single-avatar.component'
import Button from '../button/button.component'
import { BUTTON_SIZE, BUTTON_THEME, BUTTON_VARIANTS } from '../button/button.constants'
import { UpgradeTooltip } from '../feature-gate'
import { NAVIGATION_LABEL } from './navigation.constant'
import { TEST_IDS } from '@hapana/shared/constants/test-ids.constants'
import { handleLinkClick } from '@hapana/shared/web/utils/new-tab-navigation.util'
import PlusIcon from '@hapana/shared/web/styles/assets/svgs/plus-small.svg'

import CustomNavigationItem from '../custom-navigation-item/custom-navigation-item.component'
import { AVATAR_SIZE, AVATAR_TYPES, AVATAR_VARIANT } from '../avatar/avatar.constants'
import { useDialog } from '../dialog/dialog.hooks'

import { handleStylesWithProps, styles } from './navigation.styles'
import BottomNavigation from './bottom-navigation/bottom-navigation.component'

// Animation variants for navigation items
const navStaggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.05,
    },
  },
}

const navItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
}

const iconHoverTransition = { duration: 0.2, ease: 'easeOut' }
const iconHoverScale = { scale: 1.15 }

const Navigation = ({
  isLocation,
  setRoute,
  image,
  navItems,
  isOpen,
  onOpen,
  hasRouteIntegration,
  pathname,
  addNewLinkAction,
  shouldShowAddAdminLink,
  setisQuickSearch,
  bottomNavItems,
  header,
  authState,
  userData,
  topBannerHeight = 0,
  onItemClick,
  resolveHref,
}) => {
  const [openDialog, setOpenDialog] = React.useState(false)
  const handleOpen = () => {
    setOpenDialog(true)
    setisQuickSearch(true)
  }

  const handleClose = () => {
    setOpenDialog(false)
    setisQuickSearch(false)
  }

  const [open, setOpen] = useState(isOpen || false)

  // Allow parent to force-close the nav by changing the isOpen prop
  // (Navigation manages its own state, so prop changes after mount are otherwise ignored)
  useEffect(() => {
    setOpen(!!isOpen)
  }, [isOpen])
  const { isRTL } = useDirection()
  const stylesWithProps = handleStylesWithProps({ open, isLocation, isRTL, topBannerHeight })
  const [selected, setSelected] = useState(hasRouteIntegration ? {} : { home: true })
  const { t } = useTranslation(['common'])
  useDialog()

  const bottomNavigationItems = {
    isLocation,
    setRoute,
    image,
    bottomNavItems,
    isOpen,
    onOpen,
    hasRouteIntegration,
    pathname,
    addNewLinkAction,
    shouldShowAddAdminLink,
    setisQuickSearch,
    authState,
    userData,
    topBannerHeight,
    onItemClick,
  }

  const handleDrawerOpen = () => {
    onOpen?.(true) || setOpen(true)
  }
  const handleDrawerClose = () => {
    onOpen?.(false) || setOpen(false)
  }
  // State for 3-dot menu on nav items with menuActions
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuItemId, setMenuItemId] = useState(null)
  const [hoveredItemId, setHoveredItemId] = useState(null)

  const goTo = (item) => {
    // A plan-gated (locked) item is not clickable — its hover tooltip carries the upgrade message,
    // and only direct route navigation shows the feature-disabled page (OC-320 gating consistency).
    if (item?.locked) {
      return
    }
    // If an onItemClick handler is provided and returns true, skip default routing
    if (onItemClick && onItemClick(item)) {
      return
    }
    if (hasRouteIntegration && item.href) {
      return setRoute?.(item.href)
    } else if (item.url) {
      window.open(item.url, '_blank', 'noopener')
    } else {
      return
    }
    setSelected({ [item.id]: true })
  }

  const buildLinkProps = (route, navigateInApp, anchorPropName = 'component') => {
    if (!route || !resolveHref) {
      return { onClick: navigateInApp }
    }
    return {
      [anchorPropName]: 'a',
      href: resolveHref(route),
      onClick: (event) => handleLinkClick(event, navigateInApp),
    }
  }

  const getItemLinkProps = (item) =>
    buildLinkProps(
      hasRouteIntegration && !item?.locked && !item?.grayedOut ? item?.href : null,
      () => goTo(item)
    )

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const newSaleOnOpen = useCallback(
    (item) => ({
      variant: BUTTON_VARIANTS.OUTLINED,
      label: item.label,
      startIcon: item.icon,
    }),
    []
  )

  const newSaleOnClose = useCallback(
    (item) => ({
      variant: BUTTON_VARIANTS.ICON,
      icon: item.icon,
    }),
    []
  )

  const navItem = isLocation ? navItems?.withLocation : navItems?.withoutLocation

  const isUserProfileSelected = pathname?.includes('/profile')
  const isTeamAccessSelected = pathname?.includes('/team-access')

  return (
    <>
      {!isMobile ? (
        <Box sx={styles.menuRoot}>
          <MuiDrawer
            sx={stylesWithProps.muiDrawer}
            variant="permanent"
            anchor={isRTL ? 'right' : 'left'}
          >
            <Box sx={stylesWithProps.drawerHeading}>
              <>
                {/* Custom header section */}
                {header && React.cloneElement(header, { isOpen: open, isLocation })}

                {image && (
                  <Box
                    sx={[styles.logoContainer, stylesWithProps.logoContainer]}
                    onClick={image?.onClick}
                  >
                    {open ? (
                      <img alt={image.alt} src={image.openedSrc} style={styles.openedImage} />
                    ) : (
                      <img alt={image.alt} src={image.closedSrc} style={styles.closedImage} />
                    )}
                  </Box>
                )}

                {navItems?.loc &&
                  navItems.loc.map((item, index) => {
                    return (
                      <Box
                        key={`${item.id}-${index}`}
                        sx={[styles.drawerHeaderBlocks, styles.locationItemButton]}
                      >
                        <Box sx={[styles.brand, styles.gotoLocationHover]}>
                          <SvgIcon
                            sx={item?.locked ? styles.lockedMenuIcon : styles.menuIcon}
                            component={item.icon}
                          />
                          {open && (
                            <ListItemText
                              primary={
                                isLocation ? t('common:navigationLinks.locationName') : item.label
                              }
                              sx={
                                item?.locked
                                  ? isRTL
                                    ? styles.lockedItemLabelRTL
                                    : styles.lockedItemLabel
                                  : isRTL
                                    ? styles.itemLabelRTL
                                    : styles.itemLabel
                              }
                            />
                          )}
                        </Box>
                        <IconButton onClick={() => {}} sx={styles.IconButton}>
                          <SvgIcon sx={styles.menuIcon} component={ChevronRightIcon} />
                        </IconButton>
                      </Box>
                    )
                  })}
              </>
              <Divider sx={styles.dividerHeader} />
            </Box>
            <List sx={styles.menuList}>
              <ListItem
                component={motion.div}
                sx={styles.menuListTop}
                variants={navStaggerContainer}
                initial="hidden"
                animate="visible"
              >
                {navItem?.main &&
                  navItem.main.map((item, index) => {
                    // Simplified active state logic for slug-based routing
                    const isActive =
                      hasRouteIntegration &&
                      (() => {
                        // Extract route after site/corporate prefix (e.g., /s/{siteId}/ or /c/)
                        // to compare with item.href
                        const routeAfterPrefix =
                          pathname?.replace(/^\/(?:s\/[^/]+|c)/, '') || pathname

                        // For root route (/), check if we're at the root after removing prefix
                        if (item.href === '/') {
                          // Check if we're at site/corporate root or exact match
                          return (
                            routeAfterPrefix === '/' ||
                            routeAfterPrefix === '' ||
                            pathname === '/' ||
                            pathname?.match(/^\/s\/[^/]+$/) || // Site root: /s/{siteId}
                            pathname === '/c' // Corporate root
                          )
                        }

                        // Exact match for the current route
                        if (routeAfterPrefix === item.href || pathname === item.href) {
                          return true
                        }

                        // For all other routes, check if pathname starts with the href
                        return routeAfterPrefix?.startsWith(item.href)
                      })()

                    return (
                      <Box
                        component={motion.div}
                        key={`${item.id}-${index}`}
                        variants={navItemVariants}
                        sx={{ width: '100%' }}
                        onMouseEnter={() => setHoveredItemId(item.id)}
                        onMouseLeave={() => setHoveredItemId(null)}
                      >
                        <ListItemButton
                          disabled={item?.grayedOut}
                          data-testid={`nav-${item.id}`}
                          sx={[
                            styles.itemButton,
                            (isActive || selected[item.id]) && styles.selected,
                          ]}
                          {...getItemLinkProps(item)}
                        >
                          <ListItemIcon sx={styles.itemIcon}>
                            <Box
                              component={motion.div}
                              whileHover={item?.grayedOut ? undefined : iconHoverScale}
                              transition={iconHoverTransition}
                              sx={styles.motionIconContainer}
                            >
                              <SvgIcon
                                sx={item?.locked ? styles.lockedMenuIcon : styles.menuIcon}
                                component={item.icon}
                              />
                              {/* Lock badge for plan-gated items — shows in mini + expanded nav */}
                              {item?.locked && (
                                <UpgradeTooltip
                                  checkResult={item.lockedCheckResult}
                                  placement="right"
                                >
                                  <LockOutlinedIcon sx={styles.lockBadge} />
                                </UpgradeTooltip>
                              )}
                            </Box>
                          </ListItemIcon>
                          {open && (
                            <ListItemText
                              primary={item.label}
                              secondary={item.subtitle || undefined}
                              sx={
                                item?.locked
                                  ? isRTL
                                    ? styles.lockedItemLabelRTL
                                    : styles.lockedItemLabel
                                  : isRTL
                                    ? styles.itemLabelRTL
                                    : styles.itemLabel
                              }
                              secondaryTypographyProps={{
                                typography: 'small',
                                sx: {
                                  fontSize: '10px',
                                  lineHeight: 1.2,
                                  color: 'gray.64',
                                  marginTop: '1px',
                                },
                              }}
                            />
                          )}
                          {/* 3-dot menu for items with menuActions */}
                          {open && item.menuActions && hoveredItemId === item.id && (
                            <IconButton
                              size="small"
                              sx={{ padding: '2px', marginLeft: 'auto', color: 'gray.40' }}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setMenuAnchorEl(e.currentTarget)
                                setMenuItemId(item.id)
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </ListItemButton>
                        {/* Menu for this item */}
                        {item.menuActions && (
                          <Menu
                            anchorEl={menuAnchorEl}
                            open={menuItemId === item.id && Boolean(menuAnchorEl)}
                            onClose={() => {
                              setMenuAnchorEl(null)
                              setMenuItemId(null)
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            {item.menuActions.map((action) => (
                              <MenuItem
                                key={action.id}
                                onClick={() => {
                                  setMenuAnchorEl(null)
                                  setMenuItemId(null)
                                  action.onClick()
                                }}
                              >
                                <Typography typography="small">{action.label}</Typography>
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      </Box>
                    )
                  })}

                {open && navItems?.quickSearch && (
                  <Divider sx={[styles.divider, styles.customDivider]} />
                )}

                {!open && navItems?.quickSearch && (
                  <Divider sx={[styles.divider, styles.customDivider]} />
                )}
                {navItems?.quickSearch &&
                  (open ? (
                    <>
                      <Button
                        sx={styles.itemButton}
                        label={navItems?.quickSearch?.label}
                        size={BUTTON_SIZE.SMALL}
                        theme={BUTTON_THEME.SECONDARY}
                        variant={BUTTON_VARIANTS.OUTLINED}
                        startIcon={navItem?.quickSearch?.icon}
                        disabled={navItem?.quickSearch?.grayedOut}
                        onClick={handleOpen}
                        data-testid="navigation-quick-search"
                      />
                      <Modal
                        open={openDialog}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                      >
                        <Box>{navItems?.quickSearch?.component}</Box>
                      </Modal>
                    </>
                  ) : (
                    <>
                      <Button
                        sx={styles.itemButton}
                        variant={BUTTON_VARIANTS.ICON}
                        icon={navItems?.quickSearch?.icon}
                        disabled={navItem?.quickSearch?.grayedOut}
                        onClick={handleOpen}
                        stroke
                        data-testid="navigation-quick-search"
                      />
                      <Modal
                        open={openDialog}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                      >
                        <Box>{navItems?.quickSearch?.component}</Box>
                      </Modal>
                    </>
                  ))}

                {navItems?.quickSearch && <Divider sx={[styles.divider, styles.customDivider]} />}

                {open && navItem?.custom && (
                  <ListItemText sx={styles.customHeading}>
                    <Typography typography="small">
                      {t('common:navigationLinks.customPage', { count: 2 })}
                    </Typography>
                  </ListItemText>
                )}

                {navItem?.custom &&
                  (open ? (
                    <>
                      {navItem?.custom &&
                        navItem.custom.map((item, index) => {
                          // Simplified active state logic for slug-based routing
                          const isActive =
                            hasRouteIntegration &&
                            (() => {
                              // Extract route after site/corporate prefix (e.g., /s/{siteId}/ or /c/)
                              // to compare with item.href
                              const routeAfterPrefix =
                                pathname?.replace(/^\/[sc]\/[^/]+/, '') || pathname

                              // Exact match for the current route
                              if (routeAfterPrefix === item.href || pathname === item.href) {
                                return true
                              }

                              // For non-root routes, check if pathname starts with the href
                              if (item.href !== '/' && !item.href?.match(/^\/[^/]+\/?$/)) {
                                return routeAfterPrefix?.startsWith(item.href)
                              }

                              // For root/home routes (like /hapana-dev/), check exact match or trailing slash match
                              if (item.href?.match(/^\/[^/]+\/?$/)) {
                                return (
                                  routeAfterPrefix === item.href ||
                                  routeAfterPrefix === item.href.replace(/\/$/, '') ||
                                  routeAfterPrefix === item.href + '/'
                                )
                              }

                              return false
                            })()

                          return item.label === NAVIGATION_LABEL?.NEW_SALE ? (
                            <ListItemText
                              key={`${item.id}-${index}`}
                              sx={[styles.itemButton, styles.newSale]}
                            >
                              <Button
                                size={BUTTON_SIZE.MEDIUM}
                                stroke
                                theme={BUTTON_THEME.GRAY}
                                {...(open ? newSaleOnOpen(item) : newSaleOnClose(item))}
                              />
                            </ListItemText>
                          ) : (
                            <ListItemButton
                              disabled={item?.grayedOut}
                              key={`${item.id}-${index}`}
                              sx={[
                                styles.itemButton,
                                (isActive || selected[item.id]) && styles.selected,
                              ]}
                              {...getItemLinkProps(item)}
                            >
                              <ListItemIcon sx={styles.itemIcon}>
                                <Box
                                  component={motion.div}
                                  whileHover={item?.grayedOut ? undefined : iconHoverScale}
                                  transition={iconHoverTransition}
                                  sx={styles.motionIconContainer}
                                >
                                  <SvgIcon
                                    sx={item?.locked ? styles.lockedMenuIcon : styles.menuIcon}
                                    component={item.icon}
                                  />
                                </Box>
                              </ListItemIcon>
                              {open && (
                                <ListItemText
                                  primary={item.label}
                                  sx={item?.locked ? styles.lockedItemLabel : styles.itemLabel}
                                />
                              )}
                            </ListItemButton>
                          )
                        })}
                      <Divider sx={[styles.divider, styles.customDivider]} />
                      <ListItemText sx={styles.newLinkContainer}>
                        {shouldShowAddAdminLink && addNewLinkAction && (
                          <Button
                            variant={BUTTON_VARIANTS.TEXT}
                            startIcon={PlusIcon}
                            label={t('common:navigationLinks.addNewLink')}
                            sx={styles.newLinkButton}
                            onClick={addNewLinkAction}
                          />
                        )}
                      </ListItemText>
                    </>
                  ) : (
                    navItem?.custom && (
                      <CustomNavigationItem
                        items={navItem?.custom}
                        isOpen={open}
                        addNewLinkAction={addNewLinkAction}
                        shouldShowAddAdminLink={shouldShowAddAdminLink}
                      />
                    )
                  ))}

                {navItem?.custom && <Divider sx={[styles.divider, styles.customDivider]} />}
              </ListItem>

              <ListItem sx={[styles.menuListFooter, open && styles.row]}>
                {/* mainBottom items rendered directly above footer row */}
                {navItem?.mainBottom && (
                  <Box
                    sx={{
                      width: '100%',
                      paddingX: 0.5,
                      paddingBottom: 1,
                      borderBottom: '1px solid',
                      borderColor: 'gray.12',
                    }}
                  >
                    {navItem.mainBottom.map((item, index) => (
                      <Box
                        key={`${item.id}-${index}`}
                        sx={{ width: '100%' }}
                        onMouseEnter={() => setHoveredItemId(item.id)}
                        onMouseLeave={() => setHoveredItemId(null)}
                      >
                        <ListItemButton
                          disabled={item?.grayedOut}
                          data-testid={`nav-${item.id}`}
                          sx={styles.itemButton}
                          {...getItemLinkProps(item)}
                        >
                          <ListItemIcon sx={styles.itemIcon}>
                            <Box
                              component={motion.div}
                              whileHover={item?.grayedOut ? undefined : iconHoverScale}
                              transition={iconHoverTransition}
                              sx={styles.motionIconContainer}
                            >
                              <SvgIcon
                                sx={item?.locked ? styles.lockedMenuIcon : styles.menuIcon}
                                component={item.icon}
                              />
                              {/* Lock badge for plan-gated items — shows in mini + expanded nav */}
                              {item?.locked && (
                                <UpgradeTooltip
                                  checkResult={item.lockedCheckResult}
                                  placement="right"
                                >
                                  <LockOutlinedIcon sx={styles.lockBadge} />
                                </UpgradeTooltip>
                              )}
                            </Box>
                          </ListItemIcon>
                          {open && (
                            <ListItemText
                              primary={item.label}
                              secondary={item.subtitle || undefined}
                              sx={
                                item?.locked
                                  ? isRTL
                                    ? styles.lockedItemLabelRTL
                                    : styles.lockedItemLabel
                                  : isRTL
                                    ? styles.itemLabelRTL
                                    : styles.itemLabel
                              }
                              secondaryTypographyProps={{
                                typography: 'small',
                                sx: {
                                  fontSize: '10px',
                                  lineHeight: 1.2,
                                  color: 'gray.64',
                                  marginTop: '1px',
                                },
                              }}
                            />
                          )}
                          {open && item.menuActions && hoveredItemId === item.id && (
                            <IconButton
                              size="small"
                              sx={{ padding: '2px', marginLeft: 'auto', color: 'gray.40' }}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setMenuAnchorEl(e.currentTarget)
                                setMenuItemId(item.id)
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </ListItemButton>
                        {item.menuActions && (
                          <Menu
                            anchorEl={menuAnchorEl}
                            open={menuItemId === item.id && Boolean(menuAnchorEl)}
                            onClose={() => {
                              setMenuAnchorEl(null)
                              setMenuItemId(null)
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            {item.menuActions.map((action) => (
                              <MenuItem
                                key={action.id}
                                onClick={() => {
                                  setMenuAnchorEl(null)
                                  setMenuItemId(null)
                                  action.onClick()
                                }}
                              >
                                <Typography typography="small">{action.label}</Typography>
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                {navItems?.bottomDefault &&
                  navItems.bottomDefault.map((item, index) => {
                    // Simplified active state logic for slug-based routing
                    const isActive =
                      hasRouteIntegration &&
                      (() => {
                        // Extract route after site/corporate prefix (e.g., /s/{siteId}/ or /c/)
                        // to compare with item.href
                        const routeAfterPrefix =
                          pathname?.replace(/^\/(?:s\/[^/]+|c)/, '') || pathname

                        // For root route (/), check if we're at the root after removing prefix
                        if (item.href === '/') {
                          // Check if we're at site/corporate root or exact match
                          return (
                            routeAfterPrefix === '/' ||
                            routeAfterPrefix === '' ||
                            pathname === '/' ||
                            pathname?.match(/^\/s\/[^/]+$/) || // Site root: /s/{siteId}
                            pathname === '/c' // Corporate root
                          )
                        }

                        // Exact match for the current route
                        if (routeAfterPrefix === item.href || pathname === item.href) {
                          return true
                        }

                        // For all other routes, check if pathname starts with the href
                        return routeAfterPrefix?.startsWith(item.href)
                      })()

                    return (
                      <ListItemButton
                        disabled={item?.grayedOut}
                        key={`${item.id}-${index}`}
                        sx={[
                          styles.itemButtonFooter,
                          open && styles.itemButtonFooterRow,
                          (isActive || selected[item.id]) && styles.selected,
                        ]}
                      >
                        {/* The click handler lives on the icon, not the button, so the
                            test id has to ride along with it to stay clickable. */}
                        <ListItemIcon
                          sx={styles.itemIconFooter}
                          {...(item.href
                            ? buildLinkProps(item.href, () => setRoute(item.href), 'as')
                            : {})}
                          data-testid={`nav-${item.id}`}
                        >
                          <Box
                            component={motion.div}
                            whileHover={item?.grayedOut ? undefined : iconHoverScale}
                            transition={iconHoverTransition}
                            sx={styles.motionIconContainer}
                          >
                            <SvgIcon
                              sx={item?.locked ? styles.lockedMenuIcon : styles.menuIcon}
                              component={item.icon}
                            />
                          </Box>
                        </ListItemIcon>
                      </ListItemButton>
                    )
                  })}

                {isLocation && navItems?.teamAccess && (
                  <ListItemButton
                    sx={[styles.itemButtonTeamAccess, isTeamAccessSelected && styles.selected]}
                    {...buildLinkProps(navItems?.teamAccess?.route, () =>
                      setRoute(navItems?.teamAccess?.route)
                    )}
                    data-testid="nav-team-access"
                  >
                    <ListItemIcon sx={styles.itemIconFooter}>
                      <Box
                        component={motion.div}
                        whileHover={iconHoverScale}
                        transition={iconHoverTransition}
                        sx={styles.motionIconContainer}
                      >
                        <SvgIcon sx={styles.menuIcon} component={navItems?.teamAccess?.icon} />
                      </Box>
                    </ListItemIcon>
                  </ListItemButton>
                )}

                <ListItemButton
                  sx={[
                    styles.itemButtonFooter,
                    styles.itemButtonUser,
                    open && styles.itemButtonFooterRow,
                  ]}
                >
                  <ListItemIcon
                    sx={[styles.itemIconFooter, isUserProfileSelected && styles.selectedAvatar]}
                    {...buildLinkProps(
                      navItems?.userProfile?.route,
                      () => setRoute(navItems?.userProfile?.route),
                      'as'
                    )}
                  >
                    <Avatar
                      variant={AVATAR_VARIANT.CIRCULAR}
                      avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                      size={AVATAR_SIZE.MEDIUM}
                      src={navItems?.userProfile?.src}
                      alt={navItems?.userProfile?.alt}
                    />
                  </ListItemIcon>
                </ListItemButton>
                {!open && <Divider sx={[styles.divider, styles.footerDivider]} />}
                {open && <Divider sx={styles.verticalDivider} flexItem orientation="vertical" />}
                <ListItemButton
                  sx={[styles.itemButtonFooter]}
                  onClick={open ? handleDrawerClose : handleDrawerOpen}
                  data-testid={TEST_IDS.core.navExpandToggle}
                >
                  <ListItemIcon sx={styles.itemIconFooter}>
                    <Box
                      component={motion.div}
                      whileHover={iconHoverScale}
                      transition={iconHoverTransition}
                      sx={styles.motionIconContainer}
                    >
                      {open ? (
                        <SvgIcon
                          sx={styles.openMenuIcon}
                          component={KeyboardDoubleArrowLeftRoundedIcon}
                        />
                      ) : (
                        <SvgIcon
                          sx={styles.openMenuIcon}
                          component={KeyboardDoubleArrowRightRoundedIcon}
                        />
                      )}
                    </Box>
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            </List>
          </MuiDrawer>
        </Box>
      ) : (
        bottomNavItems && <BottomNavigation {...bottomNavigationItems} />
      )}
    </>
  )
}

Navigation.propTypes = {
  isLocation: PropTypes.bool,
  setRoute: PropTypes.func,
  navItems: PropTypes.shape({
    loc: PropTypes.array,
    withLocation: PropTypes.object,
    withoutLocation: PropTypes.object,
    bottomDefault: PropTypes.array,
    userProfile: PropTypes.shape({
      route: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    quickSearch: PropTypes.shape({
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
      label: PropTypes.string,
      component: PropTypes.node,
      grayedOut: PropTypes.bool,
    }),
  }),
  image: PropTypes.shape({
    openedSrc: PropTypes.string,
    closedSrc: PropTypes.string,
    alt: PropTypes.string,
    onClick: PropTypes.func,
  }),
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  pathname: PropTypes.string,
  hasRouteIntegration: PropTypes.bool,
  addNewLinkAction: PropTypes.func,
  shouldShowAddAdminLink: PropTypes.bool,
  setisQuickSearch: PropTypes.func,
  bottomNavItems: PropTypes.object,
  header: PropTypes.node,
  topBannerHeight: PropTypes.number,
  onItemClick: PropTypes.func,
  resolveHref: PropTypes.func,
}

Navigation.defaultProps = {
  isLocation: false,
  isOpen: false,
  onClick: () => null,
}

export default memo(Navigation)
